<script>
import { mdiArrowRight } from '@mdi/js'

export default {
    name: 'DashboardSection',
    props: {
        section: { type: Object, required: true },
        loading: { type: Boolean, default: false },
        error: { type: String, default: '' },
        loadingText: { type: String, default: 'Loading…' },
        emptyText: { type: String, default: 'No items yet.' },
        selectable: { type: Boolean, default: false },
        detailsLoading: { type: String, default: '' },
        detailsError: { type: String, default: '' }
    },
    data() {
        return { mdiArrowRight }
    },
    methods: {
        formatStatus(str) {
            if (!str) return ''
            return str.charAt(0).toUpperCase() + str.slice(1)
        }
    }
}
</script>

<template>
    <section
        :id="`dashboard-${section.id}`"
        class="dashboard-panel"
        :aria-labelledby="`dashboard-heading-${section.id}`"
    >
        <header class="dashboard-panel__header">
            <span class="dashboard-panel__icon" aria-hidden="true"
                ><v-icon>{{ section.icon }}</v-icon></span
            >
            <div>
                <h3 :id="`dashboard-heading-${section.id}`">{{ section.title }}</h3>
                <p>{{ section.description }}</p>
            </div>
        </header>
        <div v-if="loading" class="dashboard-panel__state" role="status">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
            {{ loadingText }}
        </div>
        <div v-else-if="error" class="dashboard-panel__state" role="alert">
            {{ error }}
            <button type="button" class="dashboard-retry" @click="$emit('retry')">Try again</button>
        </div>
        <div v-else-if="!section.rows.length" class="dashboard-panel__state">
            <slot name="empty">{{ emptyText }}</slot>
        </div>
        <ul v-else class="dashboard-panel__list">
            <li v-for="row in section.rows" :key="row.id || row.title" class="dashboard-item">
                <div>
                    <slot name="row-title" :row="row">
                        <button
                            v-if="selectable"
                            type="button"
                            class="dashboard-item-link"
                            :disabled="!!detailsLoading"
                            :aria-busy="detailsLoading === row.id"
                            @click="$emit('select', row.id)"
                        >
                            {{ row.title }}
                            <v-progress-circular
                                v-if="detailsLoading === row.id"
                                indeterminate
                                size="14"
                                width="2"
                                color="primary"
                            />
                        </button>
                        <strong v-else>{{ row.title }}</strong>
                    </slot>
                    <span>{{ row.detail }}</span>
                </div>
                <span class="dashboard-status" :class="`dashboard-status--${row.tone}`">
                    <span aria-hidden="true"></span>{{ formatStatus(row.status) }}
                </span>
            </li>
        </ul>
        <p v-if="detailsError" class="dashboard-panel__state" role="alert">{{ detailsError }}</p>
        <footer class="dashboard-panel__footer">
            <span>{{ section.footnote }}</span>
            <slot name="footer-action">
                <router-link :to="section.link">
                    {{ section.action }}<v-icon aria-hidden="true">{{ mdiArrowRight }}</v-icon>
                </router-link>
            </slot>
        </footer>
        <slot name="details" />
    </section>
</template>

<style scoped>
.dashboard-panel__state {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0;
    padding: 16px 22px;
    color: #bbc0cc;
    font-size: 12px;
    line-height: 1.6;
}
.dashboard-item-link,
.dashboard-retry {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    color: #f2a58b;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
    overflow-wrap: anywhere;
}
.dashboard-item-link:hover,
.dashboard-retry:hover {
    text-decoration: underline;
}
.dashboard-item-link:focus-visible,
.dashboard-retry:focus-visible,
.dashboard-panel a:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 4px;
}
.dashboard-item-link:disabled {
    opacity: 0.65;
}
.dashboard-panel {
    min-width: 0;
    overflow: hidden;
    scroll-margin-top: 90px;
    background: radial-gradient(ellipse at 100% 0, rgba(219, 81, 32, 0.08), transparent 60%),
        linear-gradient(145deg, rgba(52, 55, 65, 0.93), rgba(37, 40, 49, 0.96));
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 15px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.035);
}
.dashboard-panel__header {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 21px 22px 17px;
}
.dashboard-panel__icon {
    display: grid;
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    background: linear-gradient(145deg, rgba(219, 81, 32, 0.18), rgba(219, 81, 32, 0.04));
    border: 1px solid rgba(240, 120, 76, 0.24);
    border-radius: 11px;
    place-items: center;
}
.dashboard-panel__icon .v-icon {
    color: #f0784c;
    font-size: 21px;
}
.dashboard-panel__header h3 {
    margin: 0 0 5px;
    color: #f2f4fa;
    font-size: 17px;
    font-weight: 500;
    letter-spacing: -0.3px;
}
.dashboard-panel__header p {
    margin: 0;
    color: #adb5c6;
    font-size: 12px;
    line-height: 1.6;
}
.dashboard-panel__list {
    margin: 0;
    padding: 0 22px;
    list-style: none;
}
.dashboard-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 15px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.075);
}
.dashboard-item > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 5px;
}
.dashboard-item strong {
    color: #e9edf5;
    font-size: 13px;
    font-weight: 500;
    overflow-wrap: anywhere;
}
.dashboard-item > div > span {
    color: #98a1b2;
    font-size: 11px;
    overflow-wrap: anywhere;
}
.dashboard-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-shrink: 0;
    box-sizing: border-box;
    width: 140px;
    padding: 5px 8px;
    color: #c5cbd6;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    font-size: 10px;
    text-align: center;
    overflow-wrap: anywhere;
}
.dashboard-status > span {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    background: currentColor;
    border-radius: 50%;
}
.dashboard-status--orange {
    color: #ffb393;
    background: rgba(219, 81, 32, 0.08);
    border-color: rgba(240, 120, 76, 0.25);
}
.dashboard-status--green {
    color: #a0dcb7;
    background: rgba(88, 201, 135, 0.06);
    border-color: rgba(88, 201, 135, 0.2);
}
.dashboard-panel__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 13px 22px;
    background: rgba(17, 19, 25, 0.16);
    border-top: 1px solid rgba(255, 255, 255, 0.065);
}
.dashboard-panel__footer > span {
    color: #939daf;
    font-size: 10px;
    line-height: 1.5;
}
.dashboard-panel__footer a {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
    color: #f2a58b;
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;
}
.dashboard-panel__footer .v-icon {
    color: inherit;
    font-size: 16px;
}
.dashboard-panel__footer a:hover {
    text-decoration: underline;
}
@media (max-width: 420px) {
    .dashboard-item {
        align-items: flex-start;
        gap: 8px;
    }
    .dashboard-panel__header {
        padding: 18px 15px 14px;
    }
    .dashboard-panel__list {
        padding: 0 15px;
    }
    .dashboard-panel__footer {
        align-items: flex-start;
        flex-direction: column;
        padding: 12px 15px;
    }
}
</style>
